/**
 * parallel-editor-v2: the React mount over @docit/editor-core. Consumes ONLY editor-core's
 * public API — no ProseMirror/Yjs types appear here. Renders the Commit action (AC 4.1), the
 * attribution legend / "no silent AI" chrome (AC 5.2/5.4), the propose-lane review list with
 * a visual act-vs-propose distinction (AC 6.3), the floating bubble bar (formatting, commit,
 * first-class AI actions, live-object inserts), and the bottom PromptDock (free-form prompts
 * over the selection or the whole document, results arriving as reviewable proposals).
 */

import * as React from "react";
// parallel-editor-v2: the visual layer. Without this the surface is a naked contenteditable
// (provisional/committed indistinguishable, legend mashed together). Vite bundles it via the
// host's normal CSS-import path (same as other `import "*.css"` in knowit-main).
import "./styles.css";
import {
  type BlockType,
  type CollabStatus,
  createEditorV2,
  type Deriver,
  type EditorV2Handle,
  type InlineFormat,
  type LanePolicy,
  type ListType,
  type Proposal,
  type SelectionInfo,
} from "@docit/editor-core";
import { BubbleBar, type SelectionActionItem } from "./BubbleBar";
import { createHostApi, type EditorV2HostApi } from "./hostApi";
import {
  PromptDock,
  type PromptDockHandle,
  type PromptRunOutcome,
  type PromptRunRequest,
} from "./PromptDock";
import { buildWidgetNodeViews, WidgetInsertBar, type WidgetRenderer } from "./widgets";

/**
 * The host api lives in `./hostApi` (W6.1) so it can be built from a plain handle accessor and
 * unit-tested without a mount. Re-exported here because that is the specifier every host has
 * imported it from since AI Surfaces v2.
 */
export type {
  EditorV2HostApi,
  HostApplyOptions,
  HostApplyResult,
  HostInsertObject,
  HostWriteAuthor,
} from "./hostApi";

export interface EditorV2Props {
  documentId: string;
  /** Collaboration endpoint + JWT for the authenticated actor. */
  collab: { url: string; token: string | (() => string) };
  /**
   * Live state of the v2 room's socket. A host that renders this can tell the reader that
   * collaboration is unavailable instead of showing an editor that looks live while every
   * keystroke is discarded — the failure mode when the endpoint is not routed (W6 / R7).
   */
  onCollabStatus?: (status: CollabStatus) => void;
  /** Lane defaults down the containment chain (workspace→project→collection→document). */
  lanePolicy: LanePolicy;
  /** `user:<uuid>` of the acting human, stamped on commits. */
  currentUserId: string;
  /** Display name of the acting human (attribution on fresh typing + inserts). */
  currentUserName?: string;
  /** Empty-doc placeholder text. */
  placeholder?: string;
  /** Containment chain above this document, forwarded on AI edit intents for lane resolution. */
  scope?: { workspaceId?: string; projectId?: string; collectionId?: string };
  /**
   * Configured selection actions (the app's user/team/project/document-scoped AI actions from
   * SelectionActionsStore / AI Actions settings). The bubble bar renders THESE, not a hardcoded set.
   */
  actions?: SelectionActionItem[];
  /** Optional seed (base64 Yjs update) when re-opening. */
  seedStateBase64?: string;
  /**
   * How widget/chart NodeViews mount their React tree. Omit on React ≤18 (the legacy
   * `ReactDOM.render` pair is the default); a React-19 host passes
   * `createReact19WidgetRenderer()` from `@docit/editor-react/src/render19` — see §C4 of the
   * W6 spec for why this is a seam rather than a fork.
   */
  widgetRenderer?: WidgetRenderer;
  /** Optional thinker-backed deriver; fires on commit to precipitate truth/decision cards. */
  deriver?: Deriver;
  /** Dual-ACL gate for AI actions — resolves the acting AI participant + lane, or denies. */
  authorizeAIAction?: (req: {
    capability: string;
    from: number;
    to: number;
    requestedLane: "propose" | "act";
  }) => Promise<{
    allowed: boolean;
    lane: "act" | "propose";
    participant: { kind: "ai" | "human"; id: string; displayName: string; model?: string };
  } | null>;
  /** Runs the generation itself (the host owns the model call). */
  onAIAction?: (req: {
    capability: string;
    instruction: string;
    selectedText: string;
  }) => Promise<{ text: string } | null>;
  /** Host-owned notifications (host maps to its toast system; editor-react ships none). */
  onNotice?: (level: "info" | "success" | "error", message: string) => void;
  /**
   * Host-owned reminder persistence (AI Surfaces v2 — Nudge). The bubble-bar "Remind me…"
   * inserts a local reminder live-object AND calls this so the host can create a real,
   * server-side reminder that fires as a notification. Without it, a reminder is only an
   * in-document chip that never notifies.
   */
  onCreateNudge?: (dueIso: string, note: string) => void;
  /**
   * Bubble-bar "Taskize" — extract tasks & decisions from the current selection. The host
   * owns the extraction UI (review tray + confirm), so this just hands over the selected text.
   */
  onTaskize?: (selectedText: string) => void;
  /**
   * Hands the host a small imperative API when the editor mounts (and null on unmount) so it
   * can insert native cards for confirmed Taskize items. See {@link EditorV2HostApi}.
   */
  onReady?: (api: EditorV2HostApi | null) => void;
  /**
   * A/B metrics sink (Plan E#18). The host forwards these to its analytics layer. Emitted:
   * `opened` (cohort=v2) on mount, `time_to_first_mark` (with `ms`) on the first edit,
   * `commit`, `ai_action` (with `capability`), and `proposal_accept` / `proposal_reject`.
   */
  onMetric?: (name: string, data: Record<string, unknown>) => void;
}

const PREVIEW_MAX = 240;

export function EditorV2(props: EditorV2Props): React.JSX.Element {
  const hostRef = React.useRef<HTMLDivElement | null>(null);
  const handleRef = React.useRef<EditorV2Handle | null>(null);
  const dockRef = React.useRef<PromptDockHandle | null>(null);
  const [proposals, setProposals] = React.useState<Proposal[]>([]);
  const [selection, setSelection] = React.useState<SelectionInfo | null>(null);
  const [aiBusy, setAiBusy] = React.useState(false);
  const aiBusyRef = React.useRef(false);

  // Latest-props ref: callbacks (authorizeAIAction/onAIAction/onNotice/onMetric) never
  // retrigger the mount effect — its dep list stays [documentId, collab.url, collab.token].
  const propsRef = React.useRef(props);
  propsRef.current = props;

  React.useEffect(() => {
    if (!hostRef.current) return undefined;
    const p = propsRef.current;

    // Time-to-first-mark: from editor mount to the first content mutation (AC / E#18).
    const openedAt = Date.now();
    let firstMarkSent = false;
    p.onMetric?.("opened", { cohort: "v2", documentId: p.documentId });

    const handle = createEditorV2({
      seedStateBase64: p.seedStateBase64,
      collab: {
        url: props.collab.url,
        documentId: props.documentId,
        token: props.collab.token,
        // Read from the latest-props ref so a host may swap the handler without remounting.
        onStatus: (status) => propsRef.current.onCollabStatus?.(status),
      },
      lanePolicy: p.lanePolicy,
      localParticipant: {
        kind: "human",
        id: p.currentUserId,
        displayName: p.currentUserName ?? "You",
      },
      placeholder: p.placeholder,
      deriver: p.deriver,
      // doc-widgets W4: render widget/chart nodes with the SAME registry the v1 editor uses.
      extraNodeViews: buildWidgetNodeViews(p.widgetRenderer),
      // Stable, monotonic timestamps without Math.random / argless Date in the engine.
      now: () => new Date().toISOString(),
      onChange: () => {
        if (!firstMarkSent) {
          firstMarkSent = true;
          const cur = propsRef.current;
          cur.onMetric?.("time_to_first_mark", {
            cohort: "v2",
            documentId: cur.documentId,
            ms: Date.now() - openedAt,
          });
        }
      },
      onProposalsChanged: setProposals,
      // Fires synchronously on every dispatched transaction — keep it a bare state set.
      onSelectionChange: setSelection,
      // 'Run with AI' on a task card: prefill + focus the dock, targeting insert-after the card.
      // The objectId rides along so runDockPrompt can RE-RESOLVE the range at run time — the
      // click-time positions go stale as soon as anyone edits above the card.
      // (Edit/done/remove never reach here — the engine's NodeViews handle those internally.)
      onObjectAction: (req) => {
        const cur = propsRef.current;
        cur.onMetric?.("object_action", { kind: req.kind, action: "ai" });
        if (req.kind !== "task") return;
        const at = handle.insertAt(req.posAfter);
        dockRef.current?.focus('Do this task and write the result: "' + req.summary + '"', {
          from: at.from,
          to: at.to,
          op: "insert",
          objectId: req.objectId,
        });
      },
    });
    handle.mount(hostRef.current);
    handleRef.current = handle;

    return () => {
      handle.destroy();
      handleRef.current = null;
      setSelection(null);
    };
    // Re-mount only when the document identity or connection changes.
  }, [props.documentId, props.collab.url, props.collab.token]);

  const focusEditor = React.useCallback(() => handleRef.current?.focus(), []);

  const commitSelection = React.useCallback(() => {
    const handle = handleRef.current;
    if (!handle) return;
    if (handle.commitSelection()) {
      propsRef.current.onMetric?.("commit", {});
    }
  }, []);

  const toggleFormat = React.useCallback(
    (f: InlineFormat) => handleRef.current?.toggleFormat(f),
    [],
  );
  const setBlockType = React.useCallback((b: BlockType) => handleRef.current?.setBlockType(b), []);
  const toggleList = React.useCallback((l: ListType) => handleRef.current?.toggleList(l), []);

  /**
   * The AI action flow: snapshot selection → dual-ACL authorize → generate → dispatch as a
   * PROPOSAL (requestedLane 'propose'; lane policy fallback keeps it reviewable per design).
   */
  const runAIAction = React.useCallback(async (capability: string, instruction: string) => {
    const handle = handleRef.current;
    if (!handle || aiBusyRef.current) return;
    const p = propsRef.current;
    const info = handle.getSelectionInfo();
    if (info.empty) return;
    // Never fire a doomed request: the transform endpoint rejects a blank instruction
    // (400). A configured action with no prompt reaching here is a config gap, not a
    // user error the server should see.
    if (!instruction.trim()) {
      p.onNotice?.("error", "This action has no instruction to run.");
      return;
    }
    if (!p.authorizeAIAction || !p.onAIAction) {
      p.onNotice?.("error", "AI actions are not available for this document.");
      return;
    }
    aiBusyRef.current = true;
    setAiBusy(true);
    try {
      // Prefer applying IN PLACE (act). The server denies an 'act' intent (403) when the user
      // lacks the update capability, so on denial retry as a reviewable 'propose' — a viewer
      // still gets a proposal to review instead of a dead-end error.
      let auth = await p.authorizeAIAction({
        capability,
        from: info.from,
        to: info.to,
        requestedLane: "act",
      });
      if (!auth?.allowed) {
        auth = await p.authorizeAIAction({
          capability,
          from: info.from,
          to: info.to,
          requestedLane: "propose",
        });
      }
      if (!auth?.allowed) {
        p.onNotice?.("error", "You are not allowed to run this AI action here.");
        return;
      }
      const gen = await p.onAIAction({ capability, instruction, selectedText: info.text });
      // Whitespace/newline-only output is as useless as none — and would otherwise turn an
      // accepted 'replace' proposal into a bare deletion of the selection.
      if (!gen?.text?.trim()) {
        p.onNotice?.("error", "The AI did not return a result.");
        return;
      }
      const result = handle.dispatchEdit({
        participant: auth.participant,
        // Server-resolved lane: 'act' for update-capable users (apply IN PLACE, provisional +
        // attributed) — the fix that makes a user-invoked action transform the selection rather than
        // file a detached proposal — or 'propose' from the propose-lane retry above (viewers).
        requestedLane: auth.lane,
        range: { from: info.from, to: info.to },
        op: "replace",
        text: gen.text,
        rationale: instruction,
        scope: { ...p.scope, documentId: p.documentId },
      });
      p.onMetric?.("ai_action", { capability, lane: result.lane });
      p.onNotice?.(
        "success",
        result.applied
          ? "Applied — provisional; review or ⌘⏎ to commit"
          : "Proposal ready — review below",
      );
    } catch (err) {
      // A rejected host call (network/500) must surface as a notice, not vanish with the busy
      // pill as an unhandled rejection (the BubbleBar click path never awaits this promise).
      p.onNotice?.("error", err instanceof Error ? err.message : "The AI action failed.");
    } finally {
      aiBusyRef.current = false;
      setAiBusy(false);
    }
  }, []);

  /**
   * The PromptDock flow — same shape as {@link runAIAction} (authorize → generate → dispatch
   * as a proposal), but over a dock-resolved target (selection, snapshot, insert-after, or
   * whole-document) with the free-form prompt as both instruction and rationale.
   */
  const runDockPrompt = React.useCallback(
    async (req: PromptRunRequest): Promise<PromptRunOutcome> => {
      const handle = handleRef.current;
      if (!handle) return { kind: "failed", message: "The editor is not ready." };
      if (aiBusyRef.current) return { kind: "failed", message: "An AI action is already running." };
      const p = propsRef.current;
      // A blank prompt would 400 at the transform endpoint — reject it here with a clear message.
      if (!req.prompt.trim()) {
        return { kind: "failed", message: "Enter a prompt first." };
      }
      if (!p.authorizeAIAction || !p.onAIAction) {
        return { kind: "failed", message: "AI actions are not available for this document." };
      }
      aiBusyRef.current = true;
      setAiBusy(true);
      try {
        // Object-anchored targets ('Run with AI' on a card) are re-resolved NOW: the click-time
        // positions shift with every edit above the card. Card gone → append at the doc end.
        let target = req.target;
        if (target.objectId) {
          const fresh =
            handle.insertAfterObject(target.objectId) ?? handle.insertAt(Number.MAX_SAFE_INTEGER);
          target = { ...target, from: fresh.from, to: fresh.to };
        }
        let auth = await p.authorizeAIAction({
          capability: "ai.custom",
          from: target.from,
          to: target.to,
          requestedLane: "act",
        });
        if (!auth?.allowed) {
          // No update capability → the server 403s the 'act' intent; retry as a reviewable proposal.
          auth = await p.authorizeAIAction({
            capability: "ai.custom",
            from: target.from,
            to: target.to,
            requestedLane: "propose",
          });
        }
        if (!auth?.allowed) {
          return { kind: "failed", message: "You are not allowed to run this AI action here." };
        }
        const gen = await p.onAIAction({
          capability: "ai.custom",
          instruction: req.prompt,
          selectedText: req.selectedText,
        });
        // Whitespace-only output is as useless as none — and an accepted empty 'replace'
        // proposal would silently delete the targeted selection.
        if (!gen?.text?.trim()) {
          return { kind: "failed", message: "The AI did not return a result." };
        }
        if (target.objectId) {
          // The generation await gave peers/the user time to edit — resolve once more so the
          // dispatched range still sits immediately after the card.
          const fresh =
            handle.insertAfterObject(target.objectId) ?? handle.insertAt(Number.MAX_SAFE_INTEGER);
          target = { ...target, from: fresh.from, to: fresh.to };
        }
        const result = handle.dispatchEdit({
          participant: auth.participant,
          // Server-resolved lane (act when the user can update; propose otherwise). Lets a dock
          // prompt over a selection transform it in place instead of only proposing.
          requestedLane: auth.lane,
          range: { from: target.from, to: target.to },
          op: target.op,
          text: gen.text,
          rationale: req.prompt,
          scope: { ...p.scope, documentId: p.documentId },
        });
        p.onMetric?.("prompt_dock_run", { op: target.op });
        if (result.proposal) {
          p.onNotice?.("success", "Proposal ready for review");
          return { kind: "proposal" };
        }
        if (result.applied) return { kind: "applied" };
        return { kind: "failed", message: result.reason ?? "The edit could not be applied." };
      } catch (err) {
        return {
          kind: "failed",
          message: err instanceof Error ? err.message : "The AI action failed.",
        };
      } finally {
        aiBusyRef.current = false;
        setAiBusy(false);
      }
    },
    [],
  );

  // Dock accessors — null-safe reads over the live handle (the dock resolves targets at run time).
  const dockGetSelectionInfo = React.useCallback(
    () => handleRef.current?.getSelectionInfo() ?? null,
    [],
  );
  const dockGetMarkdown = React.useCallback(() => handleRef.current?.getMarkdown() ?? "", []);
  const dockInsertAt = React.useCallback(
    (pos: number) => handleRef.current?.insertAt(pos) ?? null,
    [],
  );
  const focusDock = React.useCallback(() => dockRef.current?.focus(), []);

  /** 'Start a task from this' — local act via the live-object registry, no server round-trip. */
  const startTask = React.useCallback(() => {
    const handle = handleRef.current;
    if (!handle) return;
    const info = handle.getSelectionInfo();
    const seed = info.text.trim();
    handle.insertObject("task", seed ? seed.slice(0, 140) : "New task", { sourceText: info.text });
    handle.focus();
  }, []);

  /** 'Remind me of this…' — reminder chip with the due date baked into the summary. */
  const remind = React.useCallback((due: string, note: string) => {
    const handle = handleRef.current;
    if (!handle || !due) return;
    const dueDate = new Date(due);
    if (Number.isNaN(dueDate.getTime())) {
      propsRef.current.onNotice?.("error", "Enter a valid reminder time.");
      return;
    }
    handle.insertObject("reminder", note + " — " + dueDate.toLocaleString(), {
      due: dueDate.toISOString(),
      note,
    });
    // Persist a real, firing reminder (Nudge) in addition to the in-doc chip.
    propsRef.current.onCreateNudge?.(dueDate.toISOString(), note);
    handle.focus();
  }, []);

  /** 'Taskize' — hand the selection to the host, which runs extraction + its review tray. */
  const taskize = React.useCallback(() => {
    const handle = handleRef.current;
    if (!handle) return;
    const info = handle.getSelectionInfo();
    if (info.empty) return;
    propsRef.current.onTaskize?.(info.text);
  }, []);

  // Imperative API for the host: materialize confirmed Taskize items as native cards, read the
  // selection, write at a range, find the document end. Stable identity; every member reads the
  // live handle and the live props at CALL time, so registration order and remounts never matter.
  const hostApiRef = React.useRef<EditorV2HostApi>(
    createHostApi({
      getHandle: () => handleRef.current,
      getContext: () => {
        const p = propsRef.current;
        return {
          documentId: p.documentId,
          ...(p.scope ? { scope: p.scope } : {}),
          currentUserId: p.currentUserId,
          ...(p.currentUserName ? { currentUserName: p.currentUserName } : {}),
        };
      },
    }),
  );
  React.useEffect(() => {
    const p = propsRef.current;
    p.onReady?.(hostApiRef.current);
    return () => p.onReady?.(null);
  }, []);

  const acceptProposal = React.useCallback((proposalId: string) => {
    const res = handleRef.current?.acceptProposal(proposalId);
    // Null = stale range (the doc shrank/shifted since the proposal was made): the payload did
    // NOT apply and the proposal stays pending — tell the user and keep the metric honest.
    if (res == null) {
      propsRef.current.onNotice?.("error", "This proposal no longer fits the document.");
      return;
    }
    propsRef.current.onMetric?.("proposal_accept", { proposalId });
  }, []);

  const rejectProposal = React.useCallback((proposalId: string) => {
    handleRef.current?.rejectProposal(proposalId);
    propsRef.current.onMetric?.("proposal_reject", { proposalId });
  }, []);

  const pending = proposals.filter((p) => p.status === "pending");
  const showBar = selection !== null && !selection.empty && selection.rect !== null;

  // The bar is position:fixed at viewport coords captured at the last PM transaction; scrolling
  // and resizing dispatch NO transaction, so refresh the rect ourselves while the bar is shown —
  // otherwise it detaches from the selection and floats over unrelated UI.
  React.useEffect(() => {
    if (!showBar) return undefined;
    // rAF-coalesced: this fires for EVERY scroll anywhere on the page (capture), and
    // getSelectionInfo() forces two synchronous layouts (coordsAtPos ×2). Unthrottled it
    // turns plain scrolling into layout thrash while a selection is active.
    let raf = 0;
    const refresh = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        raf = 0;
        const handle = handleRef.current;
        if (handle) setSelection(handle.getSelectionInfo());
      });
    };
    window.addEventListener("scroll", refresh, { capture: true, passive: true });
    window.addEventListener("resize", refresh);
    return () => {
      if (raf) window.cancelAnimationFrame(raf);
      window.removeEventListener("scroll", refresh, true);
      window.removeEventListener("resize", refresh);
    };
  }, [showBar]);

  return (
    <div className="docit-editor-v2">
      <div className="docit-editor-v2__legend" aria-label="attribution legend">
        <span className="docit-attr-human">You</span>
        {/* "no silent AI": AI-authored spans are always visibly attributed (AC 5.4). */}
        <span className="docit-attr-ai">AI</span>
        <span className="docit-provisional">provisional</span>
        <span className="docit-committed">committed</span>
        <span className="docit-editor-v2__hint">⌘⏎ commit · select text for actions</span>
      </div>

      <div ref={hostRef} className="docit-editor-v2__surface" />

      {showBar && selection && (
        <BubbleBar
          selection={selection}
          aiBusy={aiBusy}
          onCommit={commitSelection}
          onToggleFormat={toggleFormat}
          onSetBlockType={setBlockType}
          onToggleList={toggleList}
          actions={props.actions ?? []}
          onAIAction={runAIAction}
          onAskAI={focusDock}
          onStartTask={startTask}
          onTaskize={taskize}
          onRemind={remind}
          onFocusEditor={focusEditor}
        />
      )}

      {pending.length > 0 && (
        <aside className="docit-editor-v2__proposals" aria-label="pending proposals">
          <h4>Proposed changes</h4>
          <ul>
            {pending.map((p) => (
              <li key={p.proposalId} className="docit-proposal docit-proposal--propose">
                <span className={p.author.kind === "ai" ? "docit-attr-ai" : "docit-attr-human"}>
                  {p.author.displayName}
                </span>
                <span className="docit-proposal__op">{p.op}</span>
                <button type="button" onClick={() => acceptProposal(p.proposalId)}>
                  Accept
                </button>
                <button type="button" onClick={() => rejectProposal(p.proposalId)}>
                  Reject
                </button>
                {p.rationale ? (
                  <span className="docit-proposal__rationale">{p.rationale}</span>
                ) : null}
                {p.text ? (
                  <pre className="docit-proposal__preview">
                    {p.text.length > PREVIEW_MAX ? p.text.slice(0, PREVIEW_MAX) + "…" : p.text}
                  </pre>
                ) : null}
              </li>
            ))}
          </ul>
        </aside>
      )}

      {/* doc-widgets §D6: the identical six insert entries the v1 "+" menu offers. */}
      <WidgetInsertBar onInsert={(data, alt) => handleRef.current?.insertWidget(data, alt)} />
      <PromptDock
        ref={dockRef}
        selection={selection}
        busy={aiBusy}
        getSelectionInfo={dockGetSelectionInfo}
        getMarkdown={dockGetMarkdown}
        insertAt={dockInsertAt}
        onRun={runDockPrompt}
      />
    </div>
  );
}
